import { useState, useCallback } from 'react'
import { Link, useParams } from 'react-router-dom'
import { HARDWARE_BUILDS } from '../../data/hardwareBuilds'
import type { BuildProject, BuildStep } from '../../types'
import { playScanSound } from '../../services/sounds'
import { hapticLight, hapticSuccess } from '../../utils/haptic'

function getProject(id: string): BuildProject | null {
  return HARDWARE_BUILDS.find((p) => p.id === id) ?? null
}

function BuildVisual({ completedSteps, currentStage }: { completedSteps: BuildStep[]; currentStage?: string }) {
  const emojis = completedSteps.map((s) => s.emoji).filter(Boolean)
  return (
    <div className="build-visual" aria-hidden>
      <div className="build-visual-bench" data-stage={currentStage ?? 'gather'}>
        {emojis.length > 0 ? (
          <span className="build-visual-parts">
            {emojis.map((e, i) => (
              <span key={i} className="build-visual-emoji" style={{ animationDelay: `${i * 0.05}s` }}>{e}</span>
            ))}
          </span>
        ) : (
          <span className="build-visual-empty">🪚</span>
        )}
      </div>
    </div>
  )
}

export function BuildProjectView() {
  const { projectId } = useParams<{ projectId: string }>()
  const project = projectId ? getProject(projectId) : null
  const [stepIndex, setStepIndex] = useState(0)

  const handleNext = useCallback(() => {
    if (!project) return
    hapticLight()
    playScanSound()
    if (stepIndex < project.steps.length - 1) {
      setStepIndex((i) => i + 1)
    }
  }, [project, stepIndex])

  if (!project) {
    return (
      <div className="page page--build">
        <div className="empty-state">
          <span className="empty-state-emoji" aria-hidden>🔧</span>
          <h2 className="empty-state-title">Project not found</h2>
          <Link to="/hardware/build" className="btn-large btn-primary" style={{ textDecoration: 'none' }}>
            Pick a project
          </Link>
        </div>
      </div>
    )
  }

  const steps = project.steps
  const currentStep = steps[stepIndex]
  const completedSteps = steps.slice(0, stepIndex)
  const isLastStep = stepIndex === steps.length - 1
  const isFinished = stepIndex >= steps.length
  const currentStage = currentStep?.stage

  if (isFinished && steps.length > 0) {
    hapticSuccess()
    return (
      <div className="page page--build page--build-done">
        <div className="build-done-content">
          <BuildVisual completedSteps={steps.map((s) => ({ ...s }))} currentStage="done" />
          <h1 className="build-done-title">You built it! 🎉</h1>
          <p className="build-done-name">
            <span className="build-done-emoji" aria-hidden>{project.emoji}</span>
            {project.name}
          </p>
          <p className="build-done-message">You did it in the app – and you can build it in real life too!</p>
        </div>
        <div className="build-done-actions">
          <Link to={`/hardware/build/${project.id}`} className="btn-large btn-primary" style={{ textDecoration: 'none' }}>
            Build again
          </Link>
          <Link to="/hardware/build" className="btn-large btn-secondary" style={{ textDecoration: 'none' }}>
            Pick another project
          </Link>
          <Link to="/hardware" className="btn-large btn-secondary" style={{ textDecoration: 'none' }}>
            Back to Hardware Store
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="page page--build">
      <header className="build-header">
        <Link to="/hardware/build" className="btn-back" aria-label="Back to projects">←</Link>
        <h1 className="build-project-name">
          <span aria-hidden>{project.emoji}</span> {project.name}
        </h1>
        <p className="build-progress" aria-live="polite">
          Step {stepIndex + 1} of {steps.length}
        </p>
      </header>

      <BuildVisual completedSteps={completedSteps} currentStage={currentStage} />

      <section className="build-step" aria-labelledby="build-step-label">
        <h2 id="build-step-label" className="build-step-label">{currentStep?.label}</h2>
        {currentStep?.instruction && (
          <p className="build-step-instruction">{currentStep.instruction}</p>
        )}
        <button
          type="button"
          className="btn-large btn-primary build-step-done"
          onClick={handleNext}
          aria-label={isLastStep ? 'Finish' : 'I did it! Next step'}
        >
          {isLastStep ? "I'm done!" : "I did it!"}
        </button>
      </section>
    </div>
  )
}
